// English Content for ME-SupportNet Website

export const APP_INFO = {
  name: 'ME SupportNet',
  tagline: 'Supporting and connecting people with ME',
  description: 'ME SupportNet is an app for people with ME/CFS (ME), Long-/Post-COVID, PostVac and related illnesses. It makes it easier for affected individuals to connect with supporters and other people with ME in their immediate area – low-threshold, location-based and intuitive.',
};

export const HOME_CONTENT = {
  hero: {
    title: 'ME SupportNet',
    subtitle: 'Supporting and connecting people with ME',
    tagline: '',
    description: 'ME SupportNet is an app for people with ME, Long-/Post-COVID, PostVac and related illnesses.',
    buttons: {
      learnMore: 'Learn More',
      whatIsME: 'What is ME?',
      joinUs: 'Join Us',
    },
  },
  introduction: {
    title: 'ME SupportNet',
    subtitle: 'Supporting and connecting people with ME',
    paragraphs: [
      'ME SupportNet is an app for people with ME/CFS (ME), Long-/Post-COVID, PostVac and related illnesses. It supports and connects pwME in their immediate area – low-threshold, location-based and intuitive.'
    ],
  },
  availabilityNotice: {
    title: 'Coming soon',
    text: 'This app is currently under development and will be available here soon!',
    platformsLabel: 'For Android & iOS',
  },
  pillars: {
    title: 'The Four Pillars',
    subtitle: 'What ME SupportNet offers',
    items: [
      {
        id: 'help',
        title: 'Help',
        icon: 'Favorite',
        description: 'Connect people with ME with local helpers for everyday support, e.g. doing shopping or taking them to appointments.',
      },
      {
        id: 'connect',
        title: 'Connect',
        icon: 'People',
        description: 'Connect with other affected individuals nearby for company and mutual support.',
      },
      {
        id: 'events',
        title: 'Events',
        icon: 'Event',
        description: 'Discover and organize events designed for people with ME.',
      },
      {
        id: 'resources',
        title: 'Resources',
        icon: 'MenuBook',
        description: 'A resource centre containing information on treatment approaches and medication, as well as links to external sources.',
      },
    ],
  },
  whyMatters: {
    title: 'Why Another App?',
    subtitle: 'Preventiing Symptom Exacerbation',
    description: 'In Germany alone, there are over 650,000 people affected by ME. Even minor activities can lead to severe and potentially permanent deterioration of their condition. Staying within their individual energy limits (pacing) is the central protective factor at this point in time.',
    keyPoint: 'Therefore, ME SupportNet aims to ease daily life for affected individuals through neighborhood support, connection with other pwME and information about the condition.',
  },
  whatAppOffers: {
    title: 'What ME SupportNet Offers',
    subtitle: 'An App for all Needs',
    paragraphs: [
      {
        title: 'Help',
        text: 'Connecting local helpers with affected individuals for everyday support. The app enables pwE to find the help they need for daily tasks, e.g. shopping,  household chores or getting to medical appointments.',
      },
      {
        title: 'Connect',
        text: 'Finding other affected individuals nearby for company and mutual support. The app creates a local network that reduces isolation and promotes peer experience sharing among pwME.',
      },
      {
        title: 'Events',
        text: 'Discovering or organizing events designed for pwME. The app helps find ME-friendly meetings or organize them yourself, both in person and online.',
      },
      {
        title: 'Resources',
        text: 'A comprehensive resource centre containing information on treatment approaches and medication, as well as numerous links to external sources. This helps those affected to better understand their limits and to access information relevant to their individual circumstances.',
      },
    ],
  },
  finalMessage: {
    title: 'ME SupportNet Creates Local Connection',
    subtitle: 'Targeted help and exchange – exactly where they are needed.',
  },
};

export const ABOUT_US = {
  title: 'About Us',
  mission: {
    title: 'Our Mission',
    description: 'ME SupportNet helps people with ME to find support in their daily lives and make local connections. We aim to build a network offering practical help and social connections that combats isolation and prevents a deterioration in health. In addition, ME SupportNet offers a comprehensive resource centre, as well as a clear events section with an easy-to-use calendar of events.',
  },
  vision: {
    title: 'Our Vision',
    description: 'A world where pwME have easy access to local support networks. A community where practical help, solidarity and an improved quality of life for affected individuals have become reality.',
  },
  team: {
    title: 'The Team',
    description: 'ME SupportNet is being developed by a dedicated team who understand the urgent need for better support systems.',
    members: [
      {
        name: 'Erik Zürn',
        role: 'Biologist and PR consultant',
        description: [
          'Affected since 2022 with Post-Covid and subsequently ME. On bad days confined to a dark-and-quiet bed, on good days able to work for a few hours. As a former science communicator, now on full disability pension, Erik uses his limited energy to establish new self-help groups and counsel pwME - and to develop ME SupportNet.',
        ],
      },
      {
        name: 'Birte Viermann',
        role: 'Psychologist, family caregiver',
        description: [
          'Former caregiver of her sister who died of severe ME in 2022, Birte is still active raising awareness about ME and trying to improve the quality of life of affected individuals. Birte is shocked by the conditions that pwME have to exist in and wants to use ME SupportNet to make things better.',
        ],
      },
      // Temporarily hidden - remove the comment markers to show this member again.
      // {
      //   name: 'A Developer with ME',
      //   role: 'MSc. Software Developer',
      //   description: [
      //     'A software developer who has lived with ME for several years and understands the daily challenges firsthand. With limited energy, she works on building ME SupportNet when she can, in order to create the tool that she would have needed herself.',
      //   ],
      // },
    ],
  },
  development: {
    title: 'Development Status',
    description: 'Thanks to the collaboration with an experienced app developer, ME SupportNet is progressing faster than initially expected. We are working toward a testing phase and hope to launch the first version soon.',
  },
};

export const WHAT_IS_ME = {
  title: 'What is ME?',
  description:
    'Myalgic Encephalomyelitis (ME) is a complex, neuroimmunological, chronic illness that affects approx. 71 million people worldwide¹, approximately 650,000+⁵ in Germany alone, with many cases going undiagnosed.\n This severely disabling condition is often triggered by a viral infection, though certain antibiotics and different kinds of physical trauma are also being discussed as triggers. The exact cause of the disease is still poorly understood, but various dysfunctions of the nervous system, endocrine system, immune system and other systems and organs have already been identified as contributing factors. ME can leave affected individuals completely bedridden, unable to perform even basic daily activities like eating, speaking, or tolerating light and sound.\n Despite affecting millions worldwide and being one of the most disabling chronic illnesses, ME remains largely misunderstood, underfunded, and often dismissed by healthcare providers.',
  keyFacts: {
    title: 'Key Facts',
    description: 'Understanding the fundamental aspects of this complex illness',
    cards: [
      {
        icon: 'People',
        title: 'Global Impact',
        description: 'Affects approximately 71 million people worldwide¹, with many cases going undiagnosed.',
      },
      {
        icon: 'Virus',
        title: 'Viral Triggers',
        description: 'Triggered often by viral infections including COVID-19, Epstein-Barr virus, and other pathogens.',
      },
      {
        icon: 'Health',
        title: 'Severe Disability',
        description: 'Can leave pwME completely bedridden, unable to perform basic daily activities.',
      },
    ],
  },
  pemDescription:
    'Post-exertional Neuroimmune Exhaustion (PENE) is the cardinal symptom of ME.⁷ This means an exacerbation of symptoms after even minimal physical, mental or emotional exertion. PENE can last for days, weeks, or even permanently, making it impossible for pwME to maintain normal activity levels.',
  whyThisMatters: {
    title: 'Why ME Awareness Matters',
    description:
      'Understanding ME is crucial because it affects millions of people worldwide, yet remains largely misunderstood and underfunded. ME severely limits the health of affected individuals, and on top of that, pwME encounter very little understanding for their illness which is often trivialized by healthcare providers and remains underfunded. By supporting research and raising awareness, we can help improve the lives of those affected and create a future with better treatment options.',
  },
  symptoms: {
    title: 'Symptoms',
    pemTitle: '⚠️ Post-exertional Neuroimmune Exhaustion (PENE) - The Cardinal Symptom',
    otherSymptomsTitle: 'Other Symptoms',
    list: [
      'Muscle weakness',
      'Flu-like exhaustion',
      'Unrefreshing sleep',
      'Pain in muscle, joints or nerves',
      'Cognitive malfunctions (brain fog)',
      'Orthostatic intolerance (difficulty reaching and maintaining an upright position)',
      'Sensitivity to light, sound, and other stimuli',
    ],
  },
  economicImpact: {
    title: 'Economic Impact',
    description:
      'ME and Long COVID together have a large economic impact in Germany, costing approximately 63 billion euros annually². Of this total, approximately half is attributable to ME. This includes healthcare costs, lost productivity, and the financial burden on families and caregivers. Similar economic impacts have been found in other countries, with studies showing comparable costs relative to GDP.',
    source: 'https://mecfs-research.org/costreport-long-covid-and-mecfs/',
    study: {
      id: 'economicImpact',
      title: 'Economic Impact in Germany by Year',
      tableHeaders: {
        year: 'Year',
        cost: 'Cost',
        gdpImpactPercent: 'GDP Impact %',
      },
      data: [
        { year: '2022', cost: '58 billion €', gdpImpact: 1.4 },
        { year: '2023', cost: '61 billion €', gdpImpact: 1.5 },
        { year: '2024', cost: '63 billion €', gdpImpact: 1.5 },
        { year: '2025 (estimated)', cost: '65 billion €', gdpImpact: 1.6 },
      ],
      source: 'https://mecfs-research.org/costreport-long-covid-and-mecfs/',
      buttonText: 'View Study',
    },
  },
  mostDisabling: {
    title: 'ME - One of the Most Disabling Chronic Illnesses',
    description:
      'ME is considered one of the most disabling chronic illnesses, with many people with ME experiencing a quality of life worse than those with multiple sclerosis, cancer, or heart disease⁴. Living with ME means living with constant pain and other symptoms. People with severe ME are often completely bedridden, unable to care for themselves and describe their state as feeling "buried alive".',
    source: 'https://mecfs-research.org/researchfunding-munich/',
    study: {
      id: 'healthIndex',
      title: 'Health Index Comparison',
      explanation:
        'The EQ-5D 3L is a standardized measure that assesses physical, mental, and social well-being. Scores below 0.5 indicate significant health impairment, while scores above 0.7 represent relatively good health status.',
      data: [
        { condition: 'ME', score: 0.47 },
        { condition: 'COPD', score: 0.65 },
        { condition: 'Multiple Sclerosis', score: 0.67 },
        { condition: 'Rheumatoid Arthritis', score: 0.69 },
        { condition: 'Lung Cancer', score: 0.69 },
        { condition: 'Stroke', score: 0.71 },
        { condition: 'Diabetes Type II', score: 0.72 },
        { condition: 'Colon Cancer', score: 0.74 },
        { condition: 'General Population', score: 0.85 },
      ],
      source: 'https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0132421',
      buttonText: 'View Study',
    },
  },
  prevalence: {
    title: 'ME - A Common, Yet Hidden Illness',
    description:
      'Despite being largely invisible in public discourse, ME is surprisingly common. ME affects an estimated ~71 million people worldwide¹, representing approximately 0.9% of the global population. Yet it remains largely unrecognized and misunderstood by the general public and healthcare systems and receives little attention and funding.',
    globalStats: {
      title: 'Global Prevalence',
      tableHeaders: {
        region: 'Region',
        cases: 'Cases',
        populationPercent: 'Population %',
      },
      data: [
        { region: 'Worldwide', cases: '~71 million', percentage: '0.9%' },
        { region: 'United States', cases: '3-4 million', percentage: '1-1.2%' },
        { region: 'Germany', cases: '~650,000', percentage: '0.8%' },
      ],
    },
    underdiagnosis: {
      title: 'The Hidden Prevalence',
      description:
        'These numbers likely represent only a fraction of the true prevalence, as ME is notoriously underdiagnosed. Many people with ME are misdiagnosed with depression, anxiety, or other conditions, while others never receive a diagnosis at all. The actual number of people affected could be significantly higher.',
    },
    sourcesLabel: 'Sources:',
    sources: [
      {
        name: 'Germany – report in Ärzteblatt',
        url: 'https://www.aerzteblatt.de/news/medienbericht-long-covid-und-mecfs-kosten-gesellschaft-63-milliarden-euro-jahrlich-ac6ab36c-b2e4-40a1-8f27-6b52d53dd9e8',
      },
      {
        name: 'US - NIH ME Research',
        url: 'https://www.nih.gov/advancing-mecfs-research',
      },
      {
        name: 'Global - NIH ME Study',
        url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11907965/',
      },
    ],
  },
  longCovid: {
    title: 'Long COVID & ME',
    statement:
      'Studies indicate that after approximately six months of Long COVID, about 50% of patients meet the diagnostic criteria for ME.⁸',
    description: 'ME is the most severe form of Long COVID, but COVID-19 is just one of many triggers for ME.',
    keyPoints: [
      'ME is the most severe form of Long COVID',
      'COVID-19 is just one of many triggers',
      'Other triggers include Epstein-Barr virus, enteroviruses, herpes viruses and certain antibiotics',
      'The underlying disease mechanism is most probably the same regardless of trigger',
      'ME has been documented for decades before COVID-19',
    ],
  },
  livingWithME: {
    title: 'What Does Living with ME Mean?',
    description:
      "Living with ME, on top of huge health challenges, means facing a healthcare system that often doesn't understand or believe your illness exists. Many pwME struggle to find doctors who can help, and most treatments are experimental or unavailable.",
    stages: [
      {
        severity: 'Mild',
        description:
          'Symptoms significantly limit daily life already, leaving pwME with only a fraction of their previous performance level. They may be able to work part-time but must forgo leisure activities and most of their social life. Often difficult for others to understand their limitations.',
      },
      {
        severity: 'Moderate',
        description:
          'Mostly housebound due to severe symptoms. Unable to work and bound to rely on help for household tasks. Can only remain upright for limited periods and walk only short distances. Mobility aids like wheelchairs can improve quality of life, but insurance often denies coverage.',
      },
      {
        severity: 'Severe',
        description:
          'Bedridden most of the time, requiring a high level of support for basic daily activities and personal hygiene. Can only concentrate for very short periods and tolerate sensory stimuli (light, noise, touch) for brief durations. Many have no access to medical care as few doctors make house calls.',
      },
      {
        severity: 'Very Severe',
        description:
          'Among the most serious conditions ever. Affected individuals cannot tolerate any sensory stimuli (light, noise, touch) and must lie shielded in darkened, silent rooms. Require full-time care and sometimes need artificial feeding. There is a critical lack of adequately trained care personnel.',
      },
    ],
  },
  impactOnPatients: {
    title: 'Impact on People with ME',
    description:
      'ME affects approximately ~71 million people worldwide¹, with many cases going undiagnosed. The illness can be severely disabling, leaving many pwME bedridden or housebound.',
    additionalInfo:
      'Many pwME face disbelief from healthcare providers, struggle to find appropriate care, and are often misdiagnosed with other conditions. The lack of understanding and funding for research means that most treatments are experimental or unavailable.',
    familyImpact:
      'Families and caregivers also bear a tremendous burden, both emotionally and financially, as they support loved ones who may be completely bedridden or require constant care.',
  },
};

export const MITMACHEN = {
  title: 'Join Us',
  subtitle: 'Be there when ME SupportNet launches',
  description: 'ME SupportNet is currently in development. Sign up for the waitlist and be the first to know when the app is available. We will contact you when there is news.',
  waitlist: {
    title: 'Join the Waitlist',
    nameLabel: 'Name',
    namePlaceholder: 'Your name',
    emailLabel: 'Email address',
    emailPlaceholder: 'your@email.com',
    roleLabel: 'I am … (optional)',
    rolePlaceholder: 'Please select',
    roleOptions: [
      { value: 'affected', label: 'Person with ME' },
      { value: 'caregiver', label: 'Family member or caregiver' },
      { value: 'supporter', label: 'Supporter / Volunteer' },
      { value: 'other', label: 'Other' },
    ],
    howHeardLabel: 'How did you hear about us? (optional)',
    howHeardPlaceholder: 'e.g. Instagram, support group, recommendation …',
    deviceLabel: 'Device (optional)',
    devicePlaceholder: 'Please select',
    deviceOptions: [
      { value: 'apple', label: 'Apple (iPhone / iPad)' },
      { value: 'android', label: 'Android' },
    ],
    offersResourcesIntro: 'If you have contacts, information, or anything else — feel free to add it below. We appreciate your support!',
    offersResourcesPlaceholder: 'e.g. contact to multiplicators etc.',
    privacyLabel: 'I consent to the details I have provided being processed for the waitlist. If I select "person affected" as my role, I also explicitly consent to the processing of that health-related information (Art. 9(2)(a) GDPR).',
    privacyNote: 'Your data (name and email address) will be used to notify you when the app launches. If you share resources or contacts, we may also reach out to you about those. We do not sell your data and do not pass it on for advertising purposes. It is stored with our processors Netlify Inc. and Airtable Inc. (both USA, EU Standard Contractual Clauses); see section 4 of the privacy policy for details. You can withdraw your consent at any time with effect for the future: ',
    privacyContact: 'kontakt(at)me-support.net',
    submitButton: 'Sign me up',
    successTitle: 'You\'re on the list!',
    successMessage: 'Thank you! We will notify you as soon as ME SupportNet launches. Until then — thank you for your support.',
    errorMessage: 'Something went wrong. Please try again later or contact us directly at kontakt(at)me-support.net.',
    requiredHint: '* Required fields',
  },
  contact: {
    title: 'Any questions?',
    description: 'Do you have further questions, or would you like to support us in other ways? We welcome every message.',
    email: 'kontakt(at)me-support.net',
  },
  moreWays: {
    title: 'Other Ways to Get Involved',
    items: [
      {
        title: 'Spread the Word',
        description: 'Share information about ME SupportNet with your network. The more people know about the app, the stronger our community becomes.',
        action: 'Follow on Instagram',
      },
      {
        title: 'Support Development',
        description: 'Financial support helps us develop the app faster and ensure it remains free for all users.',
        action: 'Donate',
      },
    ],
  },
};

export const SOCIAL_MEDIA = {
  instagram: {
    handle: '@mesupportnet',
    url: 'https://www.instagram.com/mesupportnet/',
    description: 'Follow us on Instagram for updates on ME SupportNet development, ME awareness content, and community stories.',
  },
  page: {
    title: 'Follow Our Journey',
    subtitle: 'Stay updated with development progress and ME awareness content',
    callToAction: {
      title: 'Stay Connected',
      description: 'Follow our Instagram for updates on ME SupportNet development, ME awareness, and community stories.',
      button: 'Follow @mesupportnet',
    },
  },
};

export const DONATION_INFO = {
  title: 'Support ME SupportNet',
  subtitle: 'Your donation helps us bring ME SupportNet to those who need it most',
  description: 'Development costs include app development, legal consultation, server infrastructure, and ongoing maintenance. Your support makes this possible.',
  mainDonationButton: {
    title: 'Donate Now',
    description: '',
    url: 'https://www.we-aid.org/de/project-details/104/',
  },
  impactSection: {
    title: 'Your Impact',
    cards: [
      {
        title: 'App Development',
        description: 'Funds go directly toward building and testing the ME SupportNet app, ensuring it meets the needs of people with ME.',
      },
      {
        title: 'Legal & Compliance',
        description: 'Ensuring the app meets data protection standards and provides a safe space for vulnerable users.',
      },
      {
        title: 'Awareness',
        description: 'Supporting ME awareness efforts and research initiatives.',
      },
    ],
  },
};

// Legal content from RunForMECFS - same structure
export const LEGAL_CONTENT = {
  impressum: {
    title: 'Legal Notice',
    contact: {
      title: 'Information pursuant to § 5 DDG (German Digital Services Act)',
      initiative: 'ME SupportNet is an initiative of WE AID gGmbH.',
      description: 'This website is operated by:',
      responsible: 'Responsible for content:',
      contact: 'Contact:',
      name: 'WE AID gGmbH',
      address: 'Anna-Louisa-Karsch-Str. 2',
      city: '10178 Berlin',
      country: 'Germany',
      email: 'Email: kontakt(at)me-support.net',
      management: 'Represented by the management: Christian Berger, Jan Strecker',
      registerTitle: 'Company registration:',
      registerInfo: 'Registered in the commercial register',
      registerCourt: 'Registration court: Amtsgericht Berlin Charlottenburg',
      registerNumber: 'Registration number: HRB 241239 B',
      ustIdTitle: 'VAT identification number pursuant to § 27a UStG:',
      ustId: 'DE35 2372345',
    },
    purpose: {
      title: 'Purpose of the Website',
      description: 'This website provides information about ME SupportNet — an app initiative for people with Myalgic Encephalomyelitis/Chronic Fatigue Syndrome (ME/CFS). ME SupportNet is a project of WE AID gGmbH, a non-profit limited liability company.',
      goal: 'The goal is to inform about ME SupportNet and raise awareness about Myalgic Encephalomyelitis/Chronic Fatigue Syndrome (ME).',
    },
    disclaimer: {
      title: 'Disclaimer',
      content: {
        title: 'Content Responsibility:',
        text: 'The information provided serves exclusively for education and information purposes. No guarantee is given for the accuracy of medical information. For medical questions, please consult qualified doctors.',
      },
      external: {
        title: 'External Links:',
        text: 'This website contains links to external websites. The respective providers are responsible for the content of these external websites. We have no influence on their content and assume no liability for it.',
      },
      donations: {
        title: 'Donations:',
        text: 'All donations are forwarded directly to support app development and ME awareness. We ensure transparent use of funds.',
      },
    },
    copyright: {
      title: 'Copyright',
      description: 'The content of this website is protected by copyright. The use of texts, images, or other content is only permitted with express consent.',
      images: 'Image Sources: All images used come from royalty-free sources or have been used with appropriate rights.',
    },
    additional: {
      title: 'Additional Legal Information',
      links: 'Privacy Policy | Cookie Policy',
    },
  },
  privacy: {
    title: 'Privacy Policy',
    websiteIntro: {
      title: 'I. This Website — Data Processing',
      description: 'Sections 1 to 8 apply to the website me-support.net. Data processing in the ME SupportNet app is described in the second part of this policy.',
    },
    responsible: {
      title: '1. Data Controller',
      description: 'Responsible for data processing on this website and in the ME SupportNet app:',
      initiative: 'ME SupportNet is an initiative of WE AID gGmbH.',
      name: 'WE AID gGmbH',
      address: 'Anna-Louisa-Karsch-Str. 2',
      city: '10178 Berlin',
      country: 'Germany',
      email: 'Email: kontakt(at)me-support.net',
    },
    general: {
      title: '2. General Information on Data Processing',
      description: 'This privacy policy applies to (a) this website and (b) the ME SupportNet mobile app (iOS/Android). ME SupportNet is a project of WE AID gGmbH.',
      principle: 'Principle: We only collect and process data necessary for the respective purpose. Health data is processed exclusively on the basis of your explicit consent.',
    },
    automatic: {
      title: '3. Automatically Collected Data',
      server: {
        title: 'Server Log Files:',
        description: 'The following data is automatically collected by our hosting provider Netlify Inc. (512 2nd Street, San Francisco, CA 94107, USA) with each access to our website:',
        data: [
          'IP address of the accessing computer',
          'Date and time of access',
          'Name and URL of the retrieved file',
          'Amount of data transferred',
          'Message about successful retrieval',
          'Browser type and version',
          'Operating system of the user',
        ],
      },
      legal: 'Legal Basis: Art. 6 para. 1 lit. f GDPR (legitimate interest in the technical functionality and security of the website)',
      retention: 'Retention Period: This data is automatically deleted after 7 days.',
      transfer: 'Third-country transfer: Netlify Inc. is based in the USA. The transfer is based on the European Commission\'s Standard Contractual Clauses (Art. 46(2)(c) GDPR). Netlify acts for us as a processor pursuant to Art. 28 GDPR.',
    },
    waitlist: {
      title: '4. Waitlist Sign-up ("Get Involved")',
      description: 'On the "Get Involved" page you can voluntarily join the waitlist for the launch of the ME SupportNet app. We process only the information you enter into the form yourself:',
      data: [
        'Name (required)',
        'Email address (required)',
        'Role: person affected, family member or caregiver, supporter, or other (optional)',
        'Device: Apple or Android (optional)',
        'How you heard about us (optional)',
        'Free-text details about resources or contacts you would like to offer us (optional)',
        'Your consent together with the date and time of sign-up (record of consent pursuant to Art. 7(1) GDPR)',
      ],
      purpose: {
        title: 'Purpose of processing:',
        description: 'We use this information solely to inform you about the launch of the app and about significant project news. If you name resources or contacts in the free-text field, we will also get in touch with you about those. The role and device details help us plan the release. We do not pass your data on for advertising purposes and we do not sell it.',
      },
      sensitive: {
        title: 'Note on health-related information:',
        description: 'If you select "person affected" as your role, this constitutes information about your health within the meaning of Art. 9 GDPR. This entry is optional and is processed exclusively on the basis of your explicit consent (Art. 9(2)(a) GDPR). You can also submit the form without it.',
      },
      processors: {
        title: 'Recipients and processors:',
        description: 'Your form data is transmitted via a server function of our hosting provider Netlify Inc. (512 2nd Street, San Francisco, CA 94107, USA) to Airtable, where it is stored in a database. The provider is Airtable Inc., 799 Market Street, San Francisco, CA 94103, USA. Both companies act for us as processors pursuant to Art. 28 GDPR and may process the data only on our instructions. As both are based in the USA, the data is transferred to a third country; this transfer is based on the European Commission\'s Standard Contractual Clauses (Art. 46(2)(c) GDPR). Despite these safeguards, access to the data by US authorities cannot be entirely ruled out.',
      },
      legal: 'Legal basis: Your consent (Art. 6(1)(a) GDPR; where "person affected" is selected, additionally Art. 9(2)(a) GDPR). Without consent, signing up for the waitlist is not possible; you can use all other functions of the website regardless.',
      retention: 'Retention period: We store your details until the purpose ceases to apply — that is, until the app launches and you have been notified — but no longer than 24 months after your sign-up. They are deleted thereafter. If you withdraw your consent before then, we delete your details without delay.',
      withdrawal: 'Withdrawal: You can withdraw your consent at any time with effect for the future — informally by email to kontakt(at)me-support.net. The lawfulness of processing carried out up to the point of withdrawal remains unaffected.',
    },
    cookies: {
      title: '5. Cookies and Local Storage',
      necessary: {
        title: 'Preference Cookies and Local Storage:',
        description: 'If you want it to, this website remembers your language choice, your theme choice (light/dark) and your cookie decision. Technically this happens partly in a cookie (theme mode) and partly in your browser\'s local storage (language, cookie decision). Nothing is stored without your explicit consent: as long as you have not agreed, we place nothing on your device. If you decline or withdraw a consent, entries already present are deleted.',
      },
      analytics: {
        title: 'Analytics Cookies (Google Analytics 4):',
        description: 'Only if you explicitly agree in the cookie banner or the cookie settings do we use Google Analytics 4 to analyse website usage statistically (for example which pages are opened). The provider is Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland. This sets cookies (including _ga) and processes a truncated IP address as well as a pseudonymous usage profile of this website. Transfer to Google LLC in the USA cannot be ruled out; it is based on standard contractual clauses (Art. 46 GDPR) and Google LLC\'s certification under the EU-U.S. Data Privacy Framework. You can withdraw your consent at any time with future effect via the cookie settings; the analytics cookies that were set are then deleted.',
      },
      tracking: {
        title: 'No Marketing Cookies:',
        description: 'We use no advertising or marketing cookies and pass on no data for advertising purposes. Google\'s cross-device advertising features (Google Signals, ad personalisation) are switched off.',
      },
      legal: 'Legal Basis: Your consent (Art. 6 para. 1 lit. a GDPR, Sec. 25 para. 1 TDDDG). Without your consent no cookies are set and no data is stored in your browser. You can withdraw at any time via the cookie settings; this does not affect the lawfulness of processing carried out until then.',
    },
    external: {
      title: '6. External Links and Services',
      donations: {
        title: 'Donation Links:',
        description: 'This website contains links to external donation platforms. These external providers are responsible for processing your data on their websites. Please note their respective privacy policies.',
      },
      social: {
        title: 'Social Media:',
        description: 'Links to social media lead to external platforms. The privacy policies of these platforms apply to the use of their services.',
      },
    },
    rights: {
      title: '7. Your Rights as a Data Subject',
      description: 'You have the following rights under the GDPR:',
      list: [
        'Right of access (Art. 15 GDPR)',
        'Right to rectification (Art. 16 GDPR)',
        'Right to erasure (Art. 17 GDPR)',
        'Right to restriction of processing (Art. 18 GDPR)',
        'Right to data portability (Art. 20 GDPR)',
        'Right to object (Art. 21 GDPR)',
      ],
      contact: 'Contact: To exercise your rights, please contact us at the email address provided above.',
    },
    changes: {
      title: '8. Changes to this Privacy Policy',
      description: 'We reserve the right to adapt this privacy policy to changed legal situations or changes to our website or app. The current version is available on this website.',
    },
    appIntro: {
      title: 'II. ME SupportNet App — Data Processing',
      version: 'Version 1.7 · July 2026',
      description: 'The following sections are word-for-word identical to the privacy policy displayed in the ME SupportNet mobile app (iOS and Android), which users accept during registration.',
      summaryLabel: 'In brief:',
      summary: 'ME SupportNet processes particularly sensitive data because the app supports people with ME/CFS, Long/Post-Covid, PostVac and related conditions. You decide what information you share and who can see it. We use health and location data only for the app features you choose. You can withdraw your consent at any time and delete your account.',
    },
    appSections: [
      {
        heading: '1. Controller',
        body: 'ME SupportNet is an initiative of WE AID gGmbH\nAnna-Louisa-Karsch-Str. 2, 10178 Berlin\n(represented by management: Christian Berger, Jan Strecker)\n\nFor privacy-related questions: support@me-support.net',
      },
      {
        heading: '2. Data Protection Officer (DPO)',
        body: 'Internal Data Protection Officer pursuant to Art. 13(1)(b) GDPR:\n\nChristian Berger\nWE AID gGmbH\nAnna-Louisa-Karsch-Str. 2, 10178 Berlin\n\nYou may contact the DPO directly: christian@me-support.net',
      },
      {
        heading: '3. What data we process',
        body: 'We process the following categories of personal data:\n\n• Account data: email address, name, country, language – for registration and login.\n• Profile data: username, bio, interests, city, postal code, age, pronouns – for personalisation and community features.\n• Profile picture (avatar): stored on Backblaze B2 (EU Amsterdam). The image URL is reachable without login — only upload images you are comfortable sharing publicly.\n• Health-related data (special category Art. 9 GDPR): user type (patient / supporter), illness severity, treatment entries (e.g. medications, supplements, therapies) – only when voluntarily provided.\n• Community content: events, help requests/offers, friend requests.\n• Messages: end-to-end encrypted (AES-256-GCM). We cannot read their content.\n• Push notifications: device token (pseudonymous) – for delivering app notifications (new messages, event changes, news articles, friend requests) via APNs (Apple) / FCM (Google). Each category can be turned off individually in Settings. No health data in the payload.\n• Technical data: IP address (at registration for consent proof), timestamps.',
      },
      {
        heading: '4. Legal basis',
        body: '• Contract performance (Art. 6(1)(b) GDPR): for core app features (profile, networking, community content, messaging).\n• Consent (Art. 6(1)(a) GDPR): for optional features such as location matching, community feed visibility, and push notifications.\n• Explicit consent (Art. 9(2)(a) GDPR): for health-related data (user type, severity, treatment entries).\n• Legitimate interest (Art. 6(1)(f) GDPR): for security logs, error monitoring (Sentry), and misuse detection.\n• Proof of consent (Art. 7 GDPR): the timestamp and version of the accepted privacy policy are stored.\n\nYou can withdraw optional consents (location matching, push notifications) individually at any time in the Privacy Dashboard. Consent to processing your health data (Art. 9) is a prerequisite for using the app; because the app relies on this processing, you can only withdraw it by deleting your account (Settings → Delete Account).',
      },
      {
        heading: '5. Data security',
        body: '• Messages are end-to-end encrypted (E2EE). Private keys are stored exclusively on your device (iOS Keychain / Android Keystore); the server only stores public device keys and encrypted key packages it cannot decrypt itself.\n• You can optionally set up a 12-word recovery phrase to restore your chat history after switching devices – this phrase is also known only to your device; we can neither see nor recover it.\n• Health-related fields (severity, location, bio) are field-encrypted in the database (AES-256-GCM).\n• Database backups are encrypted before being uploaded to cloud storage.\n• All transmissions use HTTPS/TLS.\n• Administrative access requires mandatory two-factor authentication (TOTP) and role-based access controls; all access is audit-logged.\n• Passwords are stored as hashes – we never see them in plain text.',
      },
      {
        heading: '6. Third-party sharing',
        body: 'We do not sell your data. Third parties only receive access when necessary to operate the app. All data processors are contractually obligated to comply with GDPR (Art. 28 GDPR):\n\n• Supabase – authentication (EU, Frankfurt)\n• Railway – backend hosting (EU, Frankfurt)\n• Backblaze B2 – encrypted backups + profile avatars (EU, Amsterdam)\n• Apple APNs – iOS push notifications (USA; SCCs under Art. 46 GDPR)\n• Google FCM – Android push notifications (USA; SCCs under Art. 46 GDPR)\n• Resend – transactional emails (account verification, deletion warnings, moderation and appeal notifications); processes email address (USA; SCCs under Art. 46 GDPR + EU-U.S. Data Privacy Framework)\n• Expo / EAS – app builds and distribution (no access to user data)\n• Sentry – error monitoring, no health data (EU)\n• PostHog – anonymised usage analytics, no health data (EU)',
      },
      {
        heading: '7. Retention periods',
        body: '• Profile and community data: until account deletion.\n• Inactive accounts: if an account is not used for 12 months, we send a warning email. 30 days later the account and all personal data are deleted automatically.\n• Access logs for health data: 24 months (Art. 9 GDPR).\n• Security audit logs: 12 months (Art. 32 GDPR).\n• Consent records: 3 years after account deletion (legal obligation Art. 7 GDPR).\n\nAfter account deletion all personal data is irreversibly removed. Messages are kept in anonymised form for E2EE integrity reasons.',
      },
      {
        heading: '8. Your rights (Art. 15–22 GDPR)',
        body: 'You have the following rights:\n\n• Access (Art. 15): export your data via Settings → Profile → Download My Data.\n• Rectification (Art. 16): edit profile data at any time in the app.\n• Erasure (Art. 17): delete your account via Settings → Delete Account.\n• Restriction of processing (Art. 18).\n• Data portability (Art. 20): JSON data export.\n• Objection (Art. 21).\n\nRequests will be answered within 1 month (Art. 12(3) GDPR). To exercise your rights, contact: support@me-support.net',
        internalLink: { text: 'Account and data deletion — all routes in detail', to: '/account-deletion' },
      },
      {
        heading: '9. No automated decision-making',
        body: 'No exclusively automated processing takes place that produces legal effects concerning you or similarly significantly affects you (Art. 22 GDPR). Matching suggestions are based on profile data you enter and are not used for automated decisions.',
      },
      {
        heading: '10. Right to lodge a complaint',
        body: 'You have the right to lodge a complaint with a supervisory authority. The authority responsible for WE AID gGmbH (registered in Berlin) is:\n\nBerliner Beauftragte für Datenschutz und Informationsfreiheit\nAlt-Moabit 59–61, 10555 Berlin\nEmail: mailbox@datenschutz-berlin.de',
        externalLink: { text: 'datenschutz-berlin.de', href: 'https://www.datenschutz-berlin.de' },
      },
    ],
  },
  cookies: {
    title: 'Cookie Policy',
    what: {
      title: 'What are Cookies?',
      description: 'Cookies are small text files that are stored on your computer or mobile device when you visit a website. They help improve the website and provide you with a better user experience.',
    },
    which: {
      title: 'Which Cookies Does This Website Use?',
      necessary: {
        title: 'Technically Necessary Cookies:',
        description: 'This website works without technically necessary cookies. No session cookies are set, and nothing is stored on your device without your consent.',
      },
      preferences: {
        title: 'Preference Cookies and Local Storage:',
        description: 'If you agree, the website remembers your theme choice (light/dark) in a cookie named "themeMode" (lifetime 1 year), and your language choice and cookie decision in your browser\'s local storage ("language", "cookieConsent", "cookiePreferences", "cookieConsentData"). You can enable and disable each of these categories individually in the cookie settings.',
      },
      analytics: {
        title: 'Analytics Cookies (Google Analytics 4):',
        description: 'Only with your explicit consent do we use Google Analytics 4 (provider: Google Ireland Limited, Dublin) to analyse website usage statistically. This sets cookies such as "_ga" and "_ga_<ID>" (lifetime up to 2 years) and processes a truncated IP address. If you withdraw your consent in the cookie settings, Google Analytics is stopped and the associated cookies are deleted. Details are in our privacy policy.',
      },
      tracking: {
        title: 'No Marketing Cookies:',
        description: 'We use no advertising or marketing cookies and pass on no data for advertising purposes. Google\'s cross-device advertising features (Google Signals, ad personalisation) are switched off.',
      },
    },
    decline: {
      title: 'What Happens If You Decline Cookies?',
      description: 'If you decline, we store nothing on your device — neither cookies nor entries in your browser\'s local storage. Entries left over from earlier visits are deleted in the process. This means:',
      effects: [
        'Your language preference is not saved; the website starts in German each time you visit, unless you open a language-specific link',
        'Your theme choice (light/dark) applies to the current visit only',
        'Google Analytics is not loaded',
        'The cookie consent banner will appear again on your next visit, because we cannot record your refusal without storing something',
        'You can still use all website features normally',
      ],
    },
    manage: {
      title: 'How Can You Manage Cookies?',
      settings: {
        title: 'This Website\'s Cookie Settings:',
        description: 'You can change or fully withdraw your choice at any time on the "Cookie Settings" page. The change takes effect immediately: deselected categories are removed from your device right away.',
      },
      browser: {
        title: 'Browser Settings:',
        description: 'You can additionally manage cookies and local storage in your browser settings. There you can delete cookies or restrict cookie storage.',
      },
    },
    contact: {
      title: 'Contact',
      description: 'If you have questions about cookies or data processing on this website, please feel free to contact us:',
      email: 'Email: kontakt(at)me-support.net',
    },
  },
  terms: {
    title: "Terms of Service",
    version: "Version 2.1 · June 2026",
    intro: "These Terms of Service apply to the ME SupportNet app and are word-for-word identical to the version displayed in the app.",
    sections: [
      {
        heading: "§ 1 Provider, Scope",
        body: "These Terms of Service apply to the use of the app and the associated online services \"ME SupportNet\".\n\nThe provider is WE AID gGmbH, Anna-Louisa-Karsch-Str. 2, 10178 Berlin, Germany, email: support@me-support.net, represented by its management. Should ME SupportNet in future be operated by a different legal entity, users will be informed before any such change takes effect.\n\nDiffering terms from users do not apply unless the provider expressly agrees to them.",
      },
      {
        heading: "§ 2 Purpose and Description of Services",
        body: "ME SupportNet is a technical platform for low-threshold connection between people with ME/CFS, Long-/Post-Covid, PostVac and related conditions with supporting persons, as well as for connection among those affected.\n\nThe platform provides in particular: creating a profile, searching for nearby contacts, in-app communication, group or community features, safety guidance, and reporting problematic content or contacts.\n\nME SupportNet does not arrange medical, nursing, therapeutic, psychotherapeutic, transport-related or other professional services. The provider does not become a party to agreements, assistance, meetings, favours or other legal relationships between users.",
      },
      {
        heading: "§ 3 Eligibility and User Account",
        body: "Use is only permitted to natural persons aged 18 or over. The provider may restrict individual features if required for safety, data protection, prevention of misuse, or technical stability.\n\nRegistration information must be provided truthfully. There is no entitlement to registration or the permanent availability of specific features.\n\nUsers are obliged to keep their login credentials confidential, protect their account from third-party access, and notify the provider immediately if there are signs of misuse.\n\nUse under a pseudonym is permitted where individual features do not require verified information. There is no obligation to use a real name.",
      },
      {
        heading: "§ 4 Roles on the Platform",
        body: "Users may use the platform primarily as an affected person, as a supporting person, or in both roles.\n\nAffected persons decide for themselves what information they share about their illness, support needs, limits, location or contact preferences, and who can see that information.\n\nSupporting persons decide for themselves what kind of support they offer. They are not permitted to present themselves as medical, therapeutic, nursing or other professional practitioners without the relevant qualifications.\n\nThe provider only verifies the identity, qualifications, reliability, health status, insurance cover or personal suitability of users where a specific verification process is explicitly designated as such. General profile information, verification symbols, community endorsements or profile age are not a guarantee by the provider.",
      },
      {
        heading: "§ 5 No Medical Advice, Not an Emergency Platform",
        body: "ME SupportNet does not replace medical, psychotherapeutic, therapeutic, nursing or other professional advice, diagnosis or treatment.\n\nContent in the app, including personal accounts, group posts, chat messages, resource references or information from other users, is not individual medical advice from the provider.\n\nUsers must not start, change or discontinue medications, treatments, dosages or medically agreed measures solely on the basis of content in ME SupportNet. Health decisions should be discussed with qualified practitioners.\n\n⚠️ ME SupportNet is not intended for emergencies. In case of acute risk to life or health, the local emergency services must be contacted — in Germany, call 112. For urgent medical questions outside of acute emergencies, the out-of-hours GP service in Germany can be reached at 116117.",
      },
      {
        heading: "§ 6 Health-Related Content and Off-Label Experiences",
        body: "Users may share their own experiences regarding illness, daily life, support, symptoms, pacing or treatments, provided they comply with these Terms, the Community Rules, third-party rights and applicable law.\n\nPersonal accounts are to be understood as personal experiences. They must not be formulated as generally valid treatment recommendations, health claims, or calls to action for others.\n\nProhibited in particular are: specific dosage instructions, calls to start, discontinue or change medications, health claims, downplaying significant risks, remote medical treatment by unauthorised persons, and publishing others' health data without consent.\n\nWhen reporting off-label use, users may only share factual, experience-based accounts. Off-label use must be clarified with a doctor and may carry significant risks.",
      },
      {
        heading: "§ 7 Location, Contact and In-Person Meetings",
        body: "Location features are intended to facilitate finding contacts in the vicinity. Users decide within the app settings whether and to what extent location data is used or displayed.\n\nThe provider recommends sharing precise addresses, phone numbers and other direct contact details only after careful consideration and on a voluntary basis.\n\nMeetings, visits, errands, accompaniments or other support services take place exclusively at the individual responsibility of the users involved. The provider gives no guarantee as to the identity, behaviour, reliability, qualifications, insurance cover or safety of other users.\n\nBefore a first meeting, users should observe the safety guidance in the app — in particular: getting to know someone through the app first, carefully sharing personal details, meeting in safe locations, involving a trusted person, and using the report and block functions.",
      },
      {
        heading: "§ 8 User Obligations",
        body: "Users commit to using the platform respectfully, lawfully, with minimal data sharing and considerately.\n\nProhibited in particular are: harassment, stalking, discrimination, threats, incitement to violence, exploitation of vulnerable persons, fraud, identity deception, unsolicited commercial contact without the provider's consent, spam, malware, automated scraping, circumventing technical safeguards, and publishing unlawful content.\n\nUsers must not publish or share with third parties any personal data, health data, screenshots, chat histories, photos, addresses or other information about other persons without their consent.\n\nUsers must not use the platform to offer medical, therapeutic, nursing, financial or other services that require official permission, professional licensing or insurance, unless they meet these requirements and can demonstrate them transparently.",
      },
      {
        heading: "§ 9 Community Rules",
        body: "The Community Rules form part of these Terms. They set out in particular the requirements for respectful interaction, health-related content, data protection, in-person meetings, reports and sanctions.\n\nThe provider may display brief safety notices, warnings and contextual guidance in the app. These notices are intended to protect users and must be observed.",
      },
      {
        heading: "§ 10 Reporting Unlawful or Problematic Content and Appeals Procedure",
        body: "Users may report content, profiles, messages or conduct that is unlawful or violates these Terms. Reporting users receive an electronic confirmation of receipt with a unique reference number (\"ticket ID\") and are informed of the outcome of the review.\n\nReports should be as specific as possible and identify the content in question, the reason for the report, and available evidence. For end-to-end encrypted chats, it may be necessary for the reporting person to provide the content themselves, as the provider cannot access private messages without cause.\n\nThe provider reviews reports carefully and takes an appropriate decision. Possible measures include in particular: warnings, removal or restriction of content, suspension of individual features, temporary or permanent account suspension, and referral to the relevant authorities where legally required or necessary for protection.\n\nAffected users will receive a written statement of reasons on a durable medium, where this is legally permissible and does not endanger the safety of other persons, investigations or third-party rights.\n\n**Appeals procedure:** Within six months of receiving notice of a moderation measure, affected users may free of charge and electronically lodge an appeal via the in-app function \"Profile → Moderation actions → Lodge appeal\". Reporting users whose report has been dismissed may appeal the dismissal under \"Profile → My reports\". Appeals are reviewed by a different team member than the one who made the original decision (four-eyes principle) and are generally decided within 14 days. In addition, users have access to out-of-court dispute settlement bodies under Art. 21 DSA and the ordinary courts.",
      },
      {
        heading: "§ 11 Suspension, Deletion and Termination",
        body: "Users may delete their account at any time through the app or by notifying the provider.\n\nThe provider may temporarily suspend or terminate an account if there are concrete indications of a breach of these Terms, misuse, security risks or unlawful use. The measure taken will depend on the severity, risk of repetition and the protection interests of other users.\n\nStatutory rights to extraordinary termination remain unaffected.\n\nFollowing account deletion, personal data will be deleted or anonymised in accordance with the privacy information, unless statutory retention or legitimate evidential interests prevent this.",
      },
      {
        heading: "§ 12 Availability and Changes to the App",
        body: "The provider endeavours to provide the app reliably. However, uninterrupted, error-free or unchanged availability is not guaranteed.\n\nThe provider may modify, restrict or discontinue features where there are objective reasons for doing so, in particular: safety, data protection, prevention of misuse, technical development, legal requirements, economic viability or protection of users.\n\nSignificant changes that substantially affect ongoing use will be announced in advance, to the extent possible taking into account safety or legal reasons.",
      },
      {
        heading: "§ 13 Data Protection",
        body: "The processing of personal data is governed by the separate privacy information provided by ME SupportNet.\n\nUsers decide within the app settings what profile and health information they provide and who can see it. Users are themselves responsible for sharing information with other users.\n\nThe provider implements technical and organisational measures to protect personal data. Users remain obliged to protect their own devices, login credentials and shared information with due care.",
      },
      {
        heading: "§ 14 Rights to Content",
        body: "Users retain the rights to the content they post.\n\nThey grant the provider a simple, geographically unrestricted, royalty-free right to store, process technically, display and make available to other users the content to the extent necessary for providing the selected app features.\n\nThis licence ends in principle upon deletion of the content or account, unless legitimate retention, evidential or security interests prevent this, or the content continues to exist in permissibly anonymised form.\n\nUsers may only post content to which they are entitled and which does not infringe third-party rights.",
      },
      {
        heading: "§ 15 Provider Liability",
        body: "The provider is fully liable for intent and gross negligence, for damages arising from injury to life, body or health, when a guarantee has been assumed, and in all cases of mandatory statutory liability.\n\nFor mildly negligent breaches of material contractual obligations, the provider is liable for the foreseeable, typical damage. Material contractual obligations are obligations whose fulfilment makes proper use of the platform possible in the first place and on whose compliance users may regularly rely.\n\nOtherwise, liability for mildly negligent breaches of duty is excluded.\n\nThe provider is not liable for independent acts, omissions, statements, meetings, assistance or other arrangements between users, to the extent the provider is not responsible for these under statutory provisions.\n\nThe provider gives no warranty as to the accuracy, completeness, currency or medical suitability of content from other users. Statutory rights remain unaffected.",
      },
      {
        heading: "§ 16 User Liability",
        body: "Users are themselves responsible for their own content, information, contacts, meetings and arrangements.\n\nUsers shall indemnify the provider against third-party claims arising from a culpable breach of these Terms or third-party rights by the respective user. This does not apply where the user is not responsible for the breach.",
      },
      {
        heading: "§ 17 Changes to These Terms",
        body: "The provider may amend these Terms where there are objective reasons for doing so, in particular changes to the app, the legal situation, security requirements or the platform structure.\n\nSignificant changes will be communicated to users in good time. Where legally required or where the change is not merely beneficial or neutral, continued use will be made dependent on express consent.\n\nIf users reject a change requiring consent, the provider may terminate the agreement with reasonable notice if continuing under the previous terms is not possible or reasonable.",
      },
      {
        heading: "§ 18 Communication",
        body: "The provider may send legally significant notices by email, in the app or via other contact channels provided by the user.\n\nUsers are obliged to keep their contact details up to date to the extent necessary for account security, data protection, moderation or contractual communication.",
      },
      {
        heading: "§ 19 Consumer Dispute Resolution",
        body: "The provider is neither obliged nor willing to participate in dispute resolution proceedings before a consumer arbitration body, unless otherwise stated in an individual case.",
      },
      {
        heading: "§ 20 Final Provisions",
        body: "German law applies. Mandatory consumer protection provisions of the state in which a user is habitually resident remain unaffected.\n\nIf any provision of these Terms is invalid, the remaining provisions remain in force. The invalid provision shall be replaced by the applicable statutory provisions.\n\nJurisdiction clauses only apply to the extent permitted by law.",
      },
      {
        heading: "Annex: Community Rules and Safety Guidance",
        body: "1. Respect and Safety\nME SupportNet is a protected space for people with serious chronic illness and for those who wish to provide support. Communication should be considerate, calm, non-pressuring and free from discrimination.\n\n2. Medical Boundaries\nShare experiences, but do not give medical instructions. Do not write that a treatment is guaranteed to help, that others should take or stop a medication, or that medical advice is unnecessary.\n\n3. Data Protection in the Community\nDo not share data about other persons without their consent. This includes names, addresses, diagnoses, chat histories, screenshots, photos, medical letters and information about relatives or practitioners.\n\n4. In-Person Meetings\nOnly share precise contact details when you feel safe. Meet new contacts first in a safe location or with a trusted person. End contact if you feel uncomfortable, and use the block or report functions.\n\n5. Supporters\nSupporters may only offer assistance they can provide safely, voluntarily and lawfully. They must not offer medical, nursing or therapeutic services if they lack the required qualifications or authorisation.\n\n6. Reports\nReport content or behaviour that endangers you or others. Do not misuse the reporting system. Keep evidence only as long as necessary for the report and do not share it publicly.",
      },
    ],
  },
  accountDeletion: {
    title: 'Delete Account and Data',
    subtitle: 'ME SupportNet App — WE AID gGmbH',
    intro:
      'This page explains how to delete your ME SupportNet account and all associated data. There are two routes: directly in the app, or by email. Both result in complete deletion.',
    warning:
      'Deletion is permanent and cannot be undone. If you want to keep your data, please export it first.',
    inApp: {
      title: 'Route 1: Delete in the app',
      description: 'If you can still sign in to the app, this is the fastest route:',
      steps: [
        'Open the ME SupportNet app and sign in.',
        'Go to "Settings".',
        'Select "Delete Account".',
        'Confirm the security prompt.',
      ],
      note: 'Your account and data are deleted immediately — there is no waiting period and no way to restore them.',
    },
    byEmail: {
      title: 'Route 2: Request deletion by email',
      description:
        'If you no longer have access to the app or your account, you can also request deletion in writing:',
      steps: [
        'Send an email to support@me-support.net.',
        'Use "Account deletion" as the subject.',
        'State the email address you used to register in the app.',
      ],
      note:
        'We verify your identity via the email address you provide and then delete your account. Your request will be processed within one month at the latest (Art. 12(3) GDPR).',
      emailLabel: 'Email',
      email: 'support@me-support.net',
      emailSubject: 'Account deletion',
    },
    partial: {
      title: 'Delete individual data without deleting your account',
      description:
        'You do not have to delete your whole account to remove data. You can delete the following data specifically — each takes effect immediately and permanently:',
      items: [
        {
          label: 'Location data (postal code, city, coordinates)',
          path: 'Settings → Privacy → My Consents → withdraw location matching',
          effect:
            'Your location data is deleted immediately from your profile and from all your help requests and offers. You will no longer appear in location matching.',
        },
        {
          label: 'Push device token',
          path: 'Settings → Privacy → My Consents → withdraw push notifications',
          effect:
            'Your device token is deleted from our systems and all notification categories are switched off. You can grant consent again at any time.',
        },
        {
          label: 'Individual treatment entries (health data, Art. 9 GDPR)',
          path: 'Treatments → open entry → remove marking',
          effect:
            'That entry is deleted from your profile. Your account and all other data remain intact.',
        },
        {
          label: 'Individual help requests and offers',
          path: 'Your help profile → entry → delete',
          effect: 'The entry is removed completely.',
        },
        {
          label: 'Profile details (bio, pronouns, interests, age, username)',
          path: 'Profile → Edit profile',
          effect:
            'You can change or clear individual fields at any time. Cleared fields are removed from the database.',
        },
        {
          label: 'Blocks',
          path: 'Settings → Blocked users',
          effect: 'You can lift individual blocks at any time.',
        },
      ],
      healthNote:
        'Not individually revocable: consent to processing your health data (user type and illness severity) is a prerequisite for using the app. It can only be withdrawn by deleting your account — the app states this explicitly in the Privacy Dashboard.',
      byRequest:
        'You can also request deletion of individual data in writing: send an email to support@me-support.net with the subject "Partial deletion", the email address of your account, and which data should be removed. We process the request within one month (Art. 12(3) GDPR).',
    },
    exportFirst: {
      title: 'Save your data first',
      description:
        'You can export your data as a JSON file at any time (Art. 20 GDPR): Settings → Profile → Download My Data. This is no longer possible after deletion.',
    },
    deleted: {
      title: 'Data that is deleted',
      description: 'When you delete your account we irreversibly remove:',
      items: [
        'Your user account including email address and password',
        'Your profile with name, username, bio, pronouns, interests, age and location details',
        'Your profile picture (avatar)',
        'Your health data: user type, illness severity and all treatment entries (Art. 9 GDPR)',
        'Your help requests and help offers',
        'Your friendships and friend requests',
        'Events you created and your event participations',
        'Your chat device keys and encrypted key packages',
        'Your push notification tokens — your device receives no further notifications',
        'Users you blocked and suggestions you dismissed',
        'Validations you gave to other profiles',
        'One-to-one chats: once the other person has also left or deleted their account, the chat and its messages are removed entirely',
      ],
    },
    retained: {
      title: 'Data that remains — and why',
      description:
        'A small amount of data is retained for legal or technical reasons. It can no longer be linked to you:',
      items: [
        'Messages in group chats with remaining participants: the content stays because it is end-to-end encrypted for the other participants and we cannot technically remove it without destroying their chat history. Your name and the link to your account are deleted — you appear only as "Deleted User".',
        'Reports, moderation decisions and appeals are anonymised and kept without any reference to you.',
        'Consent records (timestamp and version of the accepted privacy policy): 3 years after deletion, because we are legally required to prove consent (Art. 7 GDPR).',
        'Access logs for health data: 24 months (Art. 9 GDPR).',
        'Security audit logs: 12 months (Art. 32 GDPR).',
        'Encrypted database backups may still contain your data until the respective backup is rotated: daily backups for up to 14 days, monthly backups for up to 12 months.',
      ],
    },
    automatic: {
      title: 'Automatic deletion of inactive accounts',
      description:
        'If you do not use your account for 12 months, we send a warning to the email address on file. If you do not sign in within 30 days, your account and all personal data are deleted automatically and completely — following the same rules described above.',
    },
    contact: {
      title: 'Questions about deletion',
      description:
        'For questions about deleting your account or about your rights under the GDPR, you can reach us and our Data Protection Officer at support@me-support.net.',
      privacyLinkText: 'Full privacy policy',
    },
  },
  cookieBanner: {
    title: 'Cookie Settings',
    description: 'This website only stores something on your device if you agree: your language and theme choice and — optionally — Google Analytics for usage statistics. If you decline, nothing is stored. You can change your choice at any time.',
    cookiePolicy: 'Cookie Policy',
    privacyPolicy: 'Privacy Policy',
    settings: 'Settings',
    acceptAll: 'Accept All',
    declineAll: 'Decline All',
  },
};

// Citations object for reference management
export const CITATIONS = {
  1: {
    title: 'Time to correct the record on the global burden of myalgic encephalomyelitis/chronic fatigue syndrome (ME/CFS)',
    authors: 'Vardaman M, Gilmour S',
    journal: 'Journal of Translational Medicine',
    year: '2025',
    url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11907965/',
    doi: '10.1186/s12967-025-06281-0'
  },
  2: {
    title: 'Economic Impact Report: Long COVID and ME/CFS',
    authors: 'ME/CFS Research Foundation',
    year: '2024',
    url: 'https://mecfs-research.org/costreport-long-covid-and-mecfs/'
  },
  3: {
    title: 'ME/CFS Funding Report',
    authors: 'ME Research UK',
    year: '2016',
    url: 'https://www.meresearch.org.uk/mecfs-funding-report/'
  },
  4: {
    title: 'Health-related quality of life in patients with chronic fatigue syndrome: an international study',
    authors: 'Nacul LC, Lacerda EM, Pheby D, et al.',
    journal: 'PLOS ONE',
    year: '2011',
    url: 'https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0132421',
    doi: '10.1371/journal.pone.0132421'
  },
  5: {
    title: 'Ärzteblatt Report: Long COVID and ME/CFS costs society 63 billion euros annually',
    authors: 'Deutsches Ärzteblatt',
    year: '2024',
    url: 'https://www.aerzteblatt.de/news/medienbericht-long-covid-und-mecfs-kosten-gesellschaft-63-milliarden-euro-jahrlich-ac6ab36c-b2e4-40a1-8f27-6b52d53dd9e8'
  },
  6: {
    title: 'BMBF Research Funding for ME/CFS',
    authors: 'ME/CFS Research Foundation',
    year: '2023',
    url: 'https://mecfs-research.org/en/bmbf-researchfunding-sep23/'
  },
  7: {
    title: 'Characterization of Post-exertional Malaise in Patients With Myalgic Encephalomyelitis/Chronic Fatigue Syndrome',
    authors: 'Stussman B, Williams A, Snow J, et al.',
    journal: 'Frontiers in Neurology',
    year: '2020',
    url: 'https://www.frontiersin.org/articles/10.3389/fneur.2020.01025/full',
    doi: '10.3389/fneur.2020.01025'
  },
  8: {
    title: 'Long COVID and ME/CFS: A systematic review and meta-analysis',
    authors: 'Multiple authors',
    journal: 'Journal of Infection',
    year: '2024',
    url: 'https://linkinghub.elsevier.com/retrieve/pii/S0163445324002317',
    doi: '10.1016/j.jinf.2024.01.001'
  }
};

export const CONTENT = {
  app: APP_INFO,
  home: HOME_CONTENT,
  aboutUs: ABOUT_US,
  whatIsME: WHAT_IS_ME,
  mitmachen: MITMACHEN,
  socialMedia: SOCIAL_MEDIA,
  donationInfo: DONATION_INFO,
  legal: LEGAL_CONTENT,
  citations: CITATIONS,
};